import { FlightCheckMarkIcon, FormFieldLabel, networkIconFor } from '@alison/ui';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { usePlatformsById } from '../../lib/workflows.hooks.js';
import { FEATURE_SCORE_MAX } from './attributesAnalysis.js';
import { CreativeDrawer } from './CreativeDrawer.js';
import { LABEL_SX, readable, toneOf } from './ScoreVisuals.js';
import {
  batchFixes,
  batchName,
  creativesAcross,
  failedCreatives,
  requestedScoringLine,
  scoredCreatives,
  scoredPlatforms,
  whenCompleted,
} from './scoredBatch.js';
import { useLatestBatch } from './useCreativeDrawer.js';

const DRAWER_WIDTH = 1180;

const CARD_SX = {
  gap: 2,
  p: 3,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const TILE_SX = {
  gap: 1,
  minWidth: 200,
  flex: 1,
  px: 3,
  py: 2,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

export interface FullAnalysisDrawerProps {
  open: boolean;
  runId: string | null;
  result: unknown;
  completedAt?: string | null;
  onClose: () => void;
  onCreativeOpened?: () => void;
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <Stack sx={{ gap: 2 }}>
      <FormFieldLabel sx={{ ...LABEL_SX, color: 'text.muted' }}>{title}</FormFieldLabel>
      {children}
    </Stack>
  );
}

export function FullAnalysisDrawer({
  open,
  runId,
  result,
  completedAt,
  onClose,
  onCreativeOpened,
}: FullAnalysisDrawerProps): ReactNode {
  const theme = useTheme();
  const known = usePlatformsById();
  const { latest } = useLatestBatch(open ? runId : null, result);
  const [opened, setOpened] = useState<{ creativeId: string; networkId: number } | null>(null);
  const platformOf = (networkId: number): { slug: string; label: string } =>
    known[networkId] ?? { slug: '', label: String(networkId) };
  const platforms = scoredPlatforms(latest, platformOf);
  const [openId, setOpenId] = useState<number | null>(null);
  const shown = platforms.find((platform) => platform.networkId === openId) ?? platforms[0];

  const name = batchName(latest);
  const everyScore = scoredCreatives(latest);
  const creatives = creativesAcross(latest);
  const failed = failedCreatives(latest);
  const finished = whenCompleted(completedAt);
  const scoredBy = requestedScoringLine(latest);
  const heading = name === null ? 'Flight Check' : `${name} – Flight Check`;
  const fixes = shown === undefined ? [] : batchFixes(latest, shown.networkId);

  const scores = new Map(
    everyScore.map((creative) => [`${creative.creativeId}:${creative.networkId}`, creative.score]),
  );
  const scoreOf = (creativeId: string, networkId: number): number | null =>
    scores.get(`${creativeId}:${networkId}`) ?? null;
  // ponytail: worst-first only; add a sort control if people ask to reorder.
  const rows = creatives
    .map((creative) => ({
      creative,
      lowest: Math.min(
        ...platforms.map(
          (platform) => scoreOf(creative.creativeId, platform.networkId) ?? Infinity,
        ),
      ),
    }))
    .filter((row) => row.lowest !== Infinity)
    .sort((left, right) => left.lowest - right.lowest)
    .map((row) => row.creative);
  const openCreative = (creativeId: string, networkId: number): void => {
    onCreativeOpened?.();
    setOpened({ creativeId, networkId });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: `min(${DRAWER_WIDTH}px, 100vw)` } } }}
      data-testid="full-analysis-drawer"
    >
      <Stack sx={{ height: '100%', bgcolor: 'background.page' }}>
        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: 'flex-start', px: 6, py: 4, bgcolor: 'background.paper' }}
        >
          <FlightCheckMarkIcon sx={{ fontSize: 32, flexShrink: 0 }} />
          <Stack sx={{ gap: 0.25, flex: 1, minWidth: 0 }}>
            <Typography variant="h3" sx={{ fontSize: 18 }}>
              {heading}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.muted' }}>
              {[
                `${rows.length} creative${rows.length === 1 ? '' : 's'} analyzed`,
                scoredBy?.replace(/^Scored by/, 'Score based on'),
                finished,
              ]
                .filter((part) => part != null && part !== '')
                .join(' · ')}
            </Typography>
          </Stack>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseOutlined sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Stack sx={{ gap: 5, px: 6, py: 4, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {platforms.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.muted' }}>
              This run scored nothing to show.
            </Typography>
          ) : (
            <>
              <Section title="SCORE BREAKDOWN">
                <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
                  {platforms.map((platform) => {
                    const Icon = networkIconFor(platform.slug, platform.label);
                    return (
                      <Stack key={platform.networkId} sx={TILE_SX}>
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                          {Icon === undefined ? null : <Icon sx={{ fontSize: 18 }} />}
                          <Typography sx={{ fontSize: 13, flex: 1 }}>{platform.label}</Typography>
                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 500,
                              color: toneOf(theme, platform.score),
                            }}
                          >
                            {readable(platform.score)}
                          </Typography>
                        </Stack>
                        {platform.halves.map((half) => (
                          <Stack
                            key={half.label}
                            direction="row"
                            sx={{ justifyContent: 'space-between' }}
                          >
                            <Typography sx={{ fontSize: 12, color: 'text.muted' }}>
                              {half.label}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                              {readable(half.value)}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    );
                  })}
                </Stack>
              </Section>

              <Section title="TOP FIXES ACROSS THE BATCH">
                {platforms.length > 1 ? (
                  <Tabs
                    value={shown?.networkId ?? false}
                    onChange={(_, value: number) => setOpenId(value)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {platforms.map((platform) => (
                      <Tab
                        key={platform.networkId}
                        value={platform.networkId}
                        label={platform.label}
                      />
                    ))}
                  </Tabs>
                ) : null}
                {fixes.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: 'text.muted' }}>
                    No weak features to fix on {shown?.label}.
                  </Typography>
                ) : (
                  <Stack sx={{ gap: 1.5 }}>
                    {fixes.map((fix) => (
                      <Stack
                        key={`${fix.feature}-${fix.recommendation}`}
                        direction="row"
                        sx={{ ...CARD_SX, alignItems: 'flex-start' }}
                      >
                        <LightbulbOutlined sx={{ fontSize: 20, color: 'primary.main', mt: 0.25 }} />
                        <Stack sx={{ gap: 0.5, flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, color: 'text.muted' }}>
                            {[fix.attribute, fix.feature].filter((part) => part !== '').join(' · ')}
                          </Typography>
                          <Typography sx={{ fontSize: 14 }}>{fix.recommendation}</Typography>
                        </Stack>
                        <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                          <Tooltip title={fix.creatives.join(', ')}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {fix.creatives.length} of {shown?.analysed ?? fix.creatives.length}{' '}
                              creatives
                            </Typography>
                          </Tooltip>
                          <Typography sx={{ fontSize: 12, color: 'text.muted' }}>
                            Avg {fix.averageScore.toFixed(1)} / {FEATURE_SCORE_MAX}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Section>

              <Section title="CREATIVES BY PLATFORM">
                <Box sx={{ ...CARD_SX, p: 0, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Creative</TableCell>
                        {platforms.map((platform) => (
                          <TableCell key={platform.networkId} align="right">
                            {platform.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((creative) => (
                        <TableRow key={creative.creativeId}>
                          <TableCell sx={{ maxWidth: 420 }}>{creative.name}</TableCell>
                          {platforms.map((platform) => {
                            const score = scoreOf(creative.creativeId, platform.networkId);
                            const label = (
                              <Typography
                                component="span"
                                sx={{ fontSize: 14, fontWeight: 600, color: toneOf(theme, score) }}
                              >
                                {readable(score)}
                              </Typography>
                            );
                            return (
                              <TableCell key={platform.networkId} align="right">
                                {score === null ? (
                                  label
                                ) : (
                                  <Link
                                    component="button"
                                    type="button"
                                    aria-label={`${creative.name} on ${platform.label}`}
                                    onClick={() =>
                                      openCreative(creative.creativeId, platform.networkId)
                                    }
                                  >
                                    {label}
                                  </Link>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Section>

              {failed.length === 0 ? null : (
                <Section title="COULDN’T BE SCORED">
                  <Stack sx={{ gap: 1.5 }}>
                    {failed.map((creative) => (
                      <Stack
                        key={`${creative.creativeId}-${creative.networkId}`}
                        direction="row"
                        sx={{ ...CARD_SX, alignItems: 'center' }}
                      >
                        <ErrorOutlineOutlined sx={{ fontSize: 20, color: 'error.main' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {creative.name}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'text.muted' }}>
                          {[platformOf(creative.networkId).label, creative.error]
                            .filter((part) => part !== null && part !== '')
                            .join(' · ')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Section>
              )}
            </>
          )}
        </Stack>
      </Stack>
      {opened === null ? null : (
        <CreativeDrawer
          open
          runId={runId}
          result={result}
          creativeId={opened.creativeId}
          networkId={opened.networkId}
          order={rows.map((creative) => creative.creativeId)}
          onOpenCreative={(creativeId, networkId) => setOpened({ creativeId, networkId })}
          onClose={() => setOpened(null)}
        />
      )}
    </Drawer>
  );
}
